<template>
  <div class="form-content">
    <div v-if="valuesOnly">
      <div v-for="(variable, index) in localItem.variables" :key="variable._key || index" class="values-row">
        <ImageVariable
          v-if="variable.type === 'image'"
          :key="`image-${variable._key || index}`"
          v-model="variable.value"
          :label="(variable.label || variable.name) + (variable.required ? ' (Required)' : '')"
          :compact="false"
          :preview="false"
          :disabled="readOnly"
          @input="onChange"
        />
        <ClaimsVariable
          v-else-if="variable.type === 'claims'"
          :key="`claims-${variable._key || index}`"
          v-model="variable.value"
          :context="currentContext"
          :item="variable"
          :disabled="readOnly"
          @input="onChange"
        />
        <BooleanVariable
          v-else-if="variable.type === 'boolean'"
          :key="`bool-${variable._key || index}`"
          v-model="variable.value"
          :label="(variable.label || variable.name) + (variable.required ? ' (Required)' : '')"
          :disabled="readOnly"
          @input="onChange"
        />
        <TextVariable
          v-else
          :key="`text-${variable._key || index}`"
          v-model="variable.value"
          :source-files="variable.sourceFiles || []"
          :label="(variable.label || variable.name) + (variable.required ? ' (Required)' : '')"
          :disabled="readOnly"
          :multiline="true"
          :context="context"
          @input="onChange"
          @update:sourceFiles="
            files => {
              variable.sourceFiles = files;
              onChange();
            }
          "
        />
      </div>
    </div>
    <div v-else>
      <div class="button">
        <Button color="primary" :disabled="isBusy" @click="addVariable">+ Variable</Button>
      </div>
      <div v-for="(variable, index) in localItem.variables" :key="variable._key || index" class="row">
        <div class="variable">
          <div class="variable-config">
            <TextField v-model="variable.name" label="Name" :disabled="isBusy" @input="onNameChanged(variable)" />
            <TextField v-model="variable.label" label="Label" :disabled="isBusy" @input="onChange" />
            <Multiselect v-model="variable.type" label="Type" :disabled="isBusy" :options="types" @input="onChange" />
          </div>
          <Checkbox v-model="variable.required" label="Required" :disabled="isBusy" @input="onChange" />
        </div>
        <div>
          <ImageVariable
            v-if="variable.type === 'image'"
            :key="`cfg-image-${variable._key || index}`"
            v-model="variable.value"
            :label="'Default Value'"
            :compact="true"
            :disabled="isBusy"
            @input="onChange"
          />
          <Checkbox
            v-else-if="variable.type === 'boolean'"
            :key="`cfg-bool-${variable._key || index}`"
            v-model="variable.value"
            label="Default values"
            class="boolean"
            :disabled="isBusy"
            @input="onChange"
          />
          <div v-else-if="variable.type === 'claims'" :key="`cfg-claims-${variable._key || index}`" class="claims">
            <Checkbox v-model="variable.independentOnly" label="Independent claims only" :disabled="isBusy" @input="onChange" />
            <Checkbox v-model="variable.singleClaimMode" label="Single claim selection" :disabled="isBusy" @input="onChange" />
            <div class="default-value">
              <Checkbox
                v-model="variable.defaultValue"
                label="Default value selector"
                :disabled="isBusy"
                @change="e => onVariableDefaultValue(variable, e)"
              />
              <div v-if="variable.defaultValue" class="options">
                <div class="option">
                  <Multiselect
                    v-model="variable.selectorWhich"
                    label="Type"
                    :searchable="false"
                    :disabled="isBusy"
                    :options="getOptions(variable, 'which')"
                    @input="onSelectorChange(variable, 'which')"
                  />
                </div>
                <div class="option">
                  <Multiselect
                    v-if="variable.selectorWhich"
                    v-model="variable.selectorProperty"
                    label="Property"
                    :searchable="false"
                    :disabled="isBusy"
                    :options="getOptions(variable, 'property')"
                    @input="onSelectorChange(variable, 'property')"
                  />
                </div>
                <div class="option">
                  <Multiselect
                    v-if="variable.selectorProperty"
                    v-model="variable.selectorValue"
                    label="Value"
                    :searchable="false"
                    :disabled="isBusy"
                    :options="getOptions(variable, variable.selectorProperty)"
                    @input="onSelectorChange(variable, 'value')"
                  />
                </div>
              </div>
            </div>
          </div>
          <TextVariable
            v-else
            :key="`cfg-text-${variable._key || index}`"
            v-model="variable.value"
            :source-files="variable.sourceFiles || []"
            :label="'Default Value'"
            :disabled="isBusy"
            :context="context"
            @input="onChange"
            @update:sourceFiles="
              files => {
                variable.sourceFiles = files;
                onChange();
              }
            "
          />
        </div>
        <div v-if="variable.type === 'image' || variable.type === 'string'" class="doc-source">
          <Multiselect
            v-model="variable.source.docCode"
            label="Doc Code"
            :searchable="false"
            :disabled="isBusy"
            :options="mappedDocumentTypes"
            @input="onChange"
          />
          <Multiselect
            v-model="variable.source.selector"
            label="Selector"
            :searchable="false"
            :disabled="isBusy"
            :options="['all', 'newest', 'oldest']"
            @input="onChange"
          />
        </div>
        <Button class="remove-button" title="Remove variable" :disabled="isBusy" @click="removeVariable(index)">
          <MdIcon name="close" />
        </Button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import TextField from '@/components/common/TextField';
import Multiselect from '@/components/common/Multiselect';
import Button from '@/components/common/Button';
import MdIcon from '@/components/common/MdIcon';
import ImageVariable from '../variables/ImageVariable';
import TextVariable from '../variables/TextVariable';
import ClaimsVariable from '../variables/ClaimsVariable.vue';
import BooleanVariable from '../variables/BooleanVariable.vue';
import Checkbox from '@/components/common/Checkbox';

export default {
  components: {
    TextField,
    Multiselect,
    Checkbox,
    Button,
    MdIcon,
    ImageVariable,
    ClaimsVariable,
    TextVariable,
    BooleanVariable
  },
  props: {
    item: {
      type: Object,
      required: true
    },
    context: {
      type: Object,
      default: () => {}
    },
    suggestions: {
      type: Array,
      default: () => []
    },
    prefix: {
      type: String,
      default: ''
    },
    valuesOnly: {
      type: Boolean,
      default: false
    },
    readOnly: {
      type: Boolean,
      default: false
    }
  },
  data() {
    const localItem = {
      ...this.item,
      variables: this.item.variables.map(v => ({
        ...v,
        source: v.source ?? { docCode: '', selector: 'all' },
        origin: v.origin || 'form',
        defaultValue: !!v.defaultValueSelector,
        selectorWhich: v.defaultValueSelector?.which ?? null,
        selectorProperty: v.defaultValueSelector?.property ?? null,
        selectorValue: v.defaultValueSelector?.value ?? null,
      }))
    };

    const genKey = () => `${Date.now()}-${Math.random()}`;

    for (const v of localItem.variables) {
      if (!Object.prototype.hasOwnProperty.call(v, '_key')) {
        Object.defineProperty(v, '_key', { value: genKey(), enumerable: false, configurable: true });
      } else if (v._key == null) {
        Object.defineProperty(v, '_key', { value: genKey(), enumerable: false, configurable: true });
      }
    }

    return {
      currentContext: this.context,
      localItem
    };
  },
  computed: {
    ...mapState({
      providers: s => s.nlp.providers || [],
      variableSelectors: s => s.nlp.variableSelectors || {},
      documentTypes: s => s.prompts.documentTypes
    }),
    ...mapGetters({
      isBusy: 'prompts/isBusy'
    }),
    variableTypes() {
      return this.localItem.variables.map(v => v.type);
    },
    // Track sourceFiles from the original prop variables so we can sync them to localItem
    propSourceFiles() {
      return this.item.variables?.map(v => v.sourceFiles) ?? [];
    },
    // Track image variable values from the original prop so we can sync them to localItem
    propImageValues() {
      return this.item.variables?.map(v => (v.type === 'image' ? v.value : null)) ?? [];
    },
    mappedDocumentTypes() {
      return [
        ' ',
        'CURRENT_DOCUMENT',
        ...Object.keys(this.documentTypes)
          .map(key => key.toUpperCase())
          .filter(key => key !== 'NONE')
      ];
    },
    types() {
      const types = [];
      const allCapabilites = [];
      this.providers?.map(p => p.capabilities).forEach(pc => allCapabilites.push(...pc));

      for (const capability of allCapabilites) {
        types.push(...(capability?.variables || []));
      }

      return [...new Set(types)];
    }
  },
  watch: {
    propImageValues(newVal) {
      newVal.forEach((value, i) => {
        const local = this.localItem.variables[i];
        if (local && local.type === 'image' && value !== null) {
          local.value = value;
        }
      });
    },
    propSourceFiles(newVal) {
      newVal.forEach((files, i) => {
        const local = this.localItem.variables[i];
        const prop = this.item.variables[i];
        if (local) {
          this.$set(local, 'sourceFiles', files || []);
          if (files && files.length && prop) {
            local.value = prop.value;
          }
        }
      });
    },
    variableTypes: {
      handler(newVariableTypes, oldVariableTypes) {
        let index = 0;
        for (const oldType of oldVariableTypes) {
          const newType = newVariableTypes[index];
          if (newType !== oldType) {
            const variable = this.localItem.variables[index];

            if (oldType === 'claims') {
              variable.independentOnly = false;
              variable.singleClaimMode = false;
              variable.defaultValue = false;
              variable.selectorWhich = undefined;
              variable.selectorProperty = undefined;
              variable.selectorValue = undefined;
            }

            switch (variable.type) {
              case 'image':
                variable.value = [];
                break;
              case 'claims':
                variable.value = null;
                break;
              case 'boolean':
                variable.value = false;
                break;
              default:
                variable.value = '';
            }
          }
          index++;
        }
      },
      deep: true // Enable deep watching
    }
  },
  methods: {
    onVariableDefaultValue(variable, enabled) {
      variable.defaultValue = enabled;

      if (!variable.defaultValue) {
        variable.selectorWhich = undefined;
        variable.selectorProperty = undefined;
        variable.selectorValue = undefined;
      }

      this.onChange();
    },
    onSelectorChange(variable, selector) {
      if (!variable.selectorWhich) {
        variable.selectorProperty = undefined;
        variable.selectorValue = undefined;
      } else if (!variable.selectorProperty || selector === 'property') {
        variable.selectorValue = undefined;
      }

      this.onChange();
    },
    getOptions(variable, value) {
      try {
        var selector = this.variableSelectors[variable.type];

        if (!selector) {
          return [];
        }

        var selectorValue = selector[value];

        if (Array.isArray(selectorValue)) {
          return ['', ...selectorValue];
        }

        return ['', ...Object.getOwnPropertyNames(selectorValue)];
      } catch (error) {
        return [];
      }
    },
    addVariable() {
      let index = 1;
      let varName = `${this.prefix}_Var_${index}`;
      while (this.localItem.variables.find(v => v.name === varName)) {
        index++;
        varName = `${this.prefix}_Var_${index}`;
      }

      const v = {
        name: varName,
        label: varName,
        type: 'string',
        value: '',
        source: {
          docCode: '',
          selector: 'all'
        }
      };

      Object.defineProperty(v, '_key', { value: `${Date.now()}-${Math.random()}`, enumerable: false, configurable: true });
      this.localItem.variables.push(v);
      this.onChange();
    },
    removeVariable(index) {
      this.localItem.variables.splice(index, 1);
      this.onChange();
    },
    onNameChanged(variable) {
      if (variable) {
        if (this.localItem.variables.filter(v => v.name == variable.name).length > 1) {
          variable.name = '_' + variable.name;
        } else {
          variable.name = variable.name.replace(/^_+/, '');
        }
      }

      this.onChange();
    },
    onChange() {
      const updated = {
        ...this.localItem,
        variables: this.localItem.variables.map(v => ({ ...v, defaultValueSelector: undefined }))
      };

      for (const variable of updated.variables) {
        if (variable.defaultValue) {
          variable.defaultValueSelector = { which: variable.selectorWhich, property: variable.selectorProperty, value: variable.selectorValue };
        } else {
          delete variable.defaultValueSelector;
        }

        delete variable.defaultValue;
        delete variable.selectorWhich;
        delete variable.selectorProperty;
        delete variable.selectorValue;
      }

      this.$emit('change', updated);
    }
  }
};
</script>

<style scoped lang="scss">
.form-content {
  overflow-y: none;
  .button {
    margin-bottom: 10px;
  }

  .values-row {
    overflow-y: hidden;
    margin-bottom: 10px;
    grid-gap: 5px;
    align-items: flex-start;
    height: 100%;

    label {
      word-wrap: break-word;
    }
  }

  .row {
    display: grid;
    grid-template-columns: auto auto 0.95fr 20px;
    margin-bottom: 10px;
    grid-gap: 5px;
    align-items: flex-start;

    .variable {
      display: grid;
      grid-template-rows: auto auto;
      grid-gap: 5px;

      .variable-config {
        display: grid;
        grid-template-columns: 100px 100px 100px;
        grid-gap: 5px;
      }
    }

    .doc-source {
      display: grid;
      grid-gap: 5px;
      .multiselect {
        width: 150px;
      }
    }

    .boolean {
      padding: 10px;
    }

    .claims {
      display: grid;
      grid-gap: 5px;
      align-self: center;
      padding: 10px;

      .default-value {
        display: grid;
        gap: 5px;

        .options {
          display: grid;
          gap: 5px;
          grid-template-columns: 1fr 1fr 1fr;
        }
      }
    }

    .remove-button {
      align-self: center;
      grid-column: 6/6;
    }
  }
}
</style>
